/**
 * Canonical cart form, hashing, and op building.
 *
 * Everything here is pure — no DOM, no network, no localStorage — because this
 * is the logic that decides whether two carts are "the same", and getting that
 * wrong manufactures false conflicts that silently overwrite a coworker's edits.
 * It is also therefore the part covered by unit tests.
 */

import type { Cart, CartItem, CartLineGroup } from "../../types/cart.js";
import { DEFAULT_WAREHOUSE_CODE, cartLineKey } from "../cartService.js";
import type { CartDisplay, CartOp, CartOpItem, PublicCartItem } from "./types.js";
import { CartOpType } from "./types.js";

/** Server limits (docs/CART_SYNC_PLAN.md §5.5). A violation 400s the whole batch. */
export const LIMITS = {
  /** Ops per batch. We chunk below this to leave headroom. */
  maxOpsPerBatch: 500,
  opsChunkSize: 400,
  /**
   * Resulting cart lines. The server enforces this on the RESULT of a batch and
   * 400s the whole thing, so the client stops before it starts rather than
   * chunking its way into a partial write.
   */
  maxItemsPerCart: 2000,
  maxQuantity: 99_999,
  titleMaxLength: 200,
  skuMaxLength: 50,
  maxUnitPriceCents: 10_000_000,
} as const;

const WAREHOUSE_CODE_PATTERN = /^[A-Z0-9]{2,5}$/;

/** A canonical cart: `variantId|warehouseCode` → quantity. */
export type CanonicalCart = Map<string, number>;

/**
 * Build display metadata with a FIXED key order.
 *
 * The server compares display objects by raw JSON.stringify, so key order is
 * part of equality. An inconsistent order turns every repeated push into a
 * content change: a version bump, a history row, and a pointless apply on every
 * other device. Fields are omitted (never sent empty) when unavailable, and
 * every value is clamped to the server's limits so a long product title cannot
 * 400 an entire batch.
 */
export function makeDisplay(input: {
  title?: string | null;
  sku?: string | null;
  unitPriceCents?: number | null;
}): CartDisplay | undefined {
  const display: { title?: string; sku?: string; unitPriceCents?: number } = {};

  const title = (input.title ?? "").trim();
  if (title) display.title = title.slice(0, LIMITS.titleMaxLength);

  const sku = (input.sku ?? "").trim();
  if (sku) display.sku = sku.slice(0, LIMITS.skuMaxLength);

  const price = input.unitPriceCents;
  if (typeof price === "number" && Number.isFinite(price) && price >= 0) {
    display.unitPriceCents = Math.min(
      Math.round(price),
      LIMITS.maxUnitPriceCents,
    );
  }

  return Object.keys(display).length > 0 ? display : undefined;
}

/** Split a canonical key back into its parts. */
export function parseKey(
  key: string,
): { variantId: number; warehouseCode: string } | null {
  const separator = key.indexOf("|");
  if (separator <= 0) return null;
  const variantId = Number(key.slice(0, separator));
  const warehouseCode = key.slice(separator + 1);
  if (!Number.isSafeInteger(variantId) || variantId <= 0) return null;
  if (!WAREHOUSE_CODE_PATTERN.test(warehouseCode)) return null;
  return { variantId, warehouseCode };
}

/** True if a key is one the server will accept as an op target. */
export function isSendableKey(key: string): boolean {
  return parseKey(key) !== null;
}

/**
 * Canonical form of the server's view of the cart.
 *
 * Skips lines the server could never have accepted, so a malformed row cannot
 * make the local and remote carts look permanently different.
 */
export function canonicalFromServer(
  items: readonly PublicCartItem[],
): CanonicalCart {
  const canonical: CanonicalCart = new Map();
  items.forEach((item) => {
    const warehouseCode =
      item.properties?._warehouse_code || DEFAULT_WAREHOUSE_CODE;
    const key = cartLineKey(item.variantId, warehouseCode);
    if (!isSendableKey(key)) return;
    canonical.set(key, (canonical.get(key) ?? 0) + item.quantity);
  });
  return canonical;
}

/**
 * Canonical form of a Shopify cart, from pre-grouped line groups.
 *
 * Takes groups rather than the raw cart so the caller is forced to have dealt
 * with duplicate lines first (see needsLineRepair).
 */
export function canonicalFromGroups(
  groups: ReadonlyMap<string, CartLineGroup>,
): CanonicalCart {
  const canonical: CanonicalCart = new Map();
  groups.forEach((group, key) => {
    if (!isSendableKey(key)) return;
    canonical.set(key, group.totalQuantity);
  });
  return canonical;
}

/**
 * Groups holding more than one Shopify line item for the same canonical key.
 *
 * This is real and reachable: a legacy quick-buy line carries no
 * `_warehouse_code` while every synced line does, and Shopify treats those as
 * separate line items. Collapsing them in the lookup hides the extra line from
 * every diff, so it never reconciles and rides through to checkout. Repair
 * before hashing or diffing.
 */
export function needsLineRepair(
  groups: ReadonlyMap<string, CartLineGroup>,
): CartLineGroup[] {
  const damaged: CartLineGroup[] = [];
  groups.forEach((group) => {
    if (group.lineKeys.length > 1) damaged.push(group);
  });
  return damaged;
}

/**
 * Stable hash of a canonical cart.
 *
 * Sorted by key and rendered as a plain string, so Shopify's line ordering —
 * which differs between an add and a fresh read — cannot produce a different
 * hash for an identical cart. A cart that hashes differently for cosmetic
 * reasons reads as "the user edited this", which is how a sync overwrites work
 * nobody changed.
 */
export function hashCanonical(canonical: CanonicalCart): string {
  const parts: string[] = [];
  Array.from(canonical.keys())
    .sort()
    .forEach((key) => {
      parts.push(`${key}=${canonical.get(key) ?? 0}`);
    });
  return parts.join(";");
}

/** Build `set`/`remove` ops that move `current` to `target`. */
export function buildLineOps(
  current: CanonicalCart,
  target: CanonicalCart,
  displayFor?: (key: string) => CartDisplay | undefined,
): CartOp[] {
  const ops: CartOp[] = [];

  current.forEach((_quantity, key) => {
    if (target.has(key)) return;
    const parsed = parseKey(key);
    if (!parsed) return;
    ops.push({
      op: CartOpType.Remove,
      variantId: parsed.variantId,
      warehouseCode: parsed.warehouseCode,
    });
  });

  target.forEach((quantity, key) => {
    if (current.get(key) === quantity) return;
    const parsed = parseKey(key);
    if (!parsed) return;
    // quantity 0 is rejected by the server — that intent is a `remove`.
    if (quantity <= 0) {
      if (current.has(key)) {
        ops.push({
          op: CartOpType.Remove,
          variantId: parsed.variantId,
          warehouseCode: parsed.warehouseCode,
        });
      }
      return;
    }
    const display = displayFor?.(key);
    ops.push({
      op: CartOpType.Set,
      variantId: parsed.variantId,
      warehouseCode: parsed.warehouseCode,
      quantity: Math.min(quantity, LIMITS.maxQuantity),
      ...(display ? { display } : {}),
    });
  });

  return ops;
}

/**
 * Split line ops into batches the server will accept.
 *
 * Whole-cart ops must travel alone, so they are never chunked — callers send
 * those one batch at a time.
 */
export function chunkOps(ops: readonly CartOp[]): CartOp[][] {
  const batches: CartOp[][] = [];
  for (let i = 0; i < ops.length; i += LIMITS.opsChunkSize) {
    batches.push(ops.slice(i, i + LIMITS.opsChunkSize));
  }
  return batches;
}

/** Canonical cart → items for a `replace`/`stashHistory` op. */
export function canonicalToItems(
  canonical: CanonicalCart,
  displayFor?: (key: string) => CartDisplay | undefined,
): CartOpItem[] {
  const items: CartOpItem[] = [];
  canonical.forEach((quantity, key) => {
    const parsed = parseKey(key);
    if (!parsed || quantity <= 0) return;
    const display = displayFor?.(key);
    items.push({
      variantId: parsed.variantId,
      warehouseCode: parsed.warehouseCode,
      quantity: Math.min(quantity, LIMITS.maxQuantity),
      ...(display ? { display } : {}),
    });
  });
  return items;
}

/** Capture display metadata for each line of a Shopify cart, keyed canonically. */
/**
 * Shopify product type marking a fee placeholder line.
 *
 * One product carries it today — "Service Charges" (`order-fees`, the only
 * variant being 42461064593468). Matching on the TYPE rather than that variant
 * id is deliberate: a second fee product is covered automatically, and the type
 * is an explicit category marker a merchandiser set on purpose. If fee lines
 * ever stop being recognised, check this string against Shopify first.
 */
export const FEE_PRODUCT_TYPE = "Fee Placeholder";

/** A fee placeholder is a charge attached to a cart, never a thing being bought. */
export function isFeeLine(item: CartItem): boolean {
  return item.product_type === FEE_PRODUCT_TYPE;
}

/**
 * Does this cart hold anything a buyer could actually purchase?
 *
 * Fees do not count. A cart holding only a service charge is empty for every
 * purpose that asks this question — nobody checks out a fee on its own, so such
 * a cart can never be the one an order was placed from.
 */
export function hasPurchasableLines(cart: Cart): boolean {
  return cart.items.some((item) => !isFeeLine(item));
}

/**
 * Shopify's cart token with its secret key stripped.
 *
 * `/cart.js` returns a full cart ID — `<token>?key=<secret>` — but the
 * orders/create webhook carries the token alone, so the server's exact-match
 * lookup never fired and no purchased cart was ever tombstoned. The symptom was
 * the worst one available: an order completes, Shopify empties the browser
 * cart, the empty-local guard in decision.ts reads that as state loss, and the
 * cart the customer just bought is restored into their next page load. Found
 * 2026-08-19 after a customer resubmitted a "still full" cart as a second order.
 *
 * Stripping here rather than only on the server is deliberate — Shopify
 * documents the key as password-grade ("ensures the protection of the buyer's
 * private data"), and it would otherwise ride in the pull's QUERY STRING
 * through every proxy log between the browser and the origin.
 */
export function bareCartToken(token: string | undefined): string | null {
  if (!token) return null;
  const bare = token.split("?")[0];
  return bare === "" ? null : bare;
}

export function displayMapFromCart(cart: Cart): Map<string, CartDisplay> {
  const map = new Map<string, CartDisplay>();
  cart.items.forEach((item) => {
    const warehouseCode =
      item.properties?._warehouse_code || DEFAULT_WAREHOUSE_CODE;
    const key = cartLineKey(item.variant_id, warehouseCode);
    if (map.has(key)) return;
    const record = item as unknown as {
      product_title?: string;
      title?: string;
      sku?: string;
      price?: number;
    };
    const display = makeDisplay({
      title: record.product_title ?? record.title ?? null,
      sku: record.sku ?? null,
      unitPriceCents: typeof record.price === "number" ? record.price : null,
    });
    if (display) map.set(key, display);
  });
  return map;
}
